<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <h2>欢迎回来，{{ user?.username }}</h2>
      <p class="subtitle">这里是您的工作台概览</p>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <h3>视频翻译</h3>
        <div class="stat-number">{{ stats.videoTranslation || 0 }}</div>
        <p>已完成任务</p>
      </div>
      <div class="stat-card">
        <h3>视频换脸</h3>
        <div class="stat-number">{{ stats.videoSwapping || 0 }}</div>
        <p>已完成任务</p>
      </div>
      <div class="stat-card">
        <h3>字幕擦除</h3>
        <div class="stat-number">{{ stats.subtitleErasure || 0 }}</div>
        <p>已完成任务</p>
      </div>
    </div>

    <div class="recent-tasks">
      <div class="section-header">
        <h3>今天的任务</h3>
        <router-link to="/workspace/tasks" class="view-all">查看全部</router-link>
      </div>
      <div class="task-list">
        <div v-if="tasks.length === 0" class="empty-state">
          暂无任务记录
        </div>
        <div v-else v-for="task in tasks" :key="task.id" class="task-item">
          <div class="task-info">
            <span class="task-name">{{ task.taskName }}</span>
            <span class="task-name">{{ formatDate(task.CreatedAt) }}</span>
            <span class="task-type">{{ getTaskTypeName(task.type) }}</span>
          </div>
          <div class="task-status" :class="task.status">
            {{ getStatusText(task.status) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getMyTaskStatistics, getTaskList } from '@/api/task'

export default {
  name: 'Dashboard',

  data() {
    return {
      stats: {
        videoTranslation: 0,
        videoSwapping: 0,
        subtitleErasure: 0
      },
      tasks: []
    }
  },

  computed: {
    ...mapState('auth', ['user'])
  },

  methods: {
    getTaskTypeName(type) {
      const types = {
        1: '视频换脸',
        2: '视频翻译',
        4: '字幕擦除'
      }
      return types[type] || type
    },

    getStatusText(status) {
      const statusMap = {
        'pending': '等待中',
        'processing': '处理中',
        'completed': '已完成',
        'failed': '失败'
      }
      return statusMap[status] || status
    },

    formatDate(date) {
      return new Date(date).toLocaleString('zh-CN', { hour12: false })
    },

    async fetchDashboardData() {
      try {
        // 获取统计数据
        const { data: statsResponse } = await getMyTaskStatistics()
        if (statsResponse.success && statsResponse.data) {
          // 初始化统计数据
          const statsData = {
            videoTranslation: 0,
            videoSwapping: 0,
            subtitleErasure: 0
          }

          // 处理返回的统计数据
          statsResponse.data.forEach(item => {
            switch (item.taskType) {
              case 2: // 视频翻译
                statsData.videoTranslation = item.totalCount
                break
              case 1: // 视频换脸
                statsData.videoSwapping = item.totalCount
                break
              case 4: // 字幕擦除
                statsData.subtitleErasure = item.totalCount
                break
            }
          })

          this.stats = statsData
        }

        // 获取今天的任务列表
        const today = new Date()
        const startDate = today.toISOString().split('T')[0] + ' 00:00:00'
        const endDate = today.toISOString().split('T')[0] + ' 23:59:59'
        
        const { data: tasksResponse } = await getTaskList({
          startDate,
          endDate,
          pageSize: 5,
          page: 1
        })

        if (tasksResponse.success && tasksResponse.data) {
          this.tasks = tasksResponse.data.list
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error)
        this.$message.error('获取数据失败')
      }
    }
  },

  created() {
    this.fetchDashboardData()
  }
}
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.dashboard-header {
  margin-bottom: 32px;
  background: var(--bg-secondary);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
}

.dashboard-header h2 {
  font-size: 28px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.dashboard-header h2::before {
  content: '';
  display: block;
  width: 4px;
  height: 24px;
  background: var(--accent-color);
  border-radius: 2px;
}

.subtitle {
  color: var(--text-secondary);
  font-size: 15px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 40px;
}

.stat-card {
  background: var(--bg-secondary);
  padding: 28px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.stat-card::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  background: var(--accent-color);
  opacity: 0.05;
  border-radius: 50%;
  transform: translate(30%, -30%);
}

.stat-card h3 {
  font-size: 16px;
  color: var(--text-secondary);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-number {
  font-size: 42px;
  font-weight: 600;
  background: linear-gradient(45deg, var(--accent-color), #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 12px;
}

.stat-card p {
  color: var(--text-secondary);
  font-size: 14px;
}

.recent-tasks {
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
}

.section-header h3 {
  font-size: 18px;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-header h3::before {
  content: '';
  display: block;
  width: 3px;
  height: 18px;
  background: var(--accent-color);
  border-radius: 1.5px;
}

.view-all {
  color: var(--accent-color);
  text-decoration: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
}

.view-all:hover {
  opacity: 0.8;
}

.view-all::after {
  content: '→';
  font-size: 16px;
}

.task-list {
  background: var(--bg-tertiary);
  border-radius: 8px;
}

.task-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.task-item:hover {
  background: var(--bg-secondary);
}

.task-item:last-child {
  border-bottom: none;
}

.task-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.task-name {
  font-weight: 500;
  color: var(--text-primary);
}

.task-type {
  color: var(--text-secondary);
  font-size: 14px;
  background: var(--bg-secondary);
  padding: 4px 12px;
  border-radius: 16px;
}

.task-status {
  font-size: 14px;
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: 500;
}

.task-status.pending {
  background: rgba(99, 102, 241, 0.1);
  color: var(--accent-color);
}

.task-status.processing {
  background: rgba(234, 179, 8, 0.1);
  color: #EAB308;
}

.task-status.completed {
  background: rgba(34, 197, 94, 0.1);
  color: #22C55E;
}

.task-status.failed {
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.empty-state {
  text-align: center;
  padding: 48px 24px;
  color: var(--text-secondary);
  font-size: 15px;
  background: var(--bg-tertiary);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .task-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .task-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .task-status {
    align-self: flex-start;
  }
}
</style> 